/**
 * Perceptual-latency timing primitives for the scan → segmentation
 * → confirm → primary-photos flow.
 *
 * The PRD's perceptual contract (non-negotiable):
 *   - TTFV  < 300 ms   (click → first DOM mutation)
 *   - TTFOD 2–4 s       (click → first `item` SSE event)
 *   - TTFF  4–8 s       (click → first `mask` SSE event)
 *   - TTLC  20–30 s     (click → last `mask` SSE event)
 *   - E2E   30–50 s     (click → Done view visible)
 *
 * This file is a minimal `performance.now()` wrapper — `startMark()`
 * anchors the session, `mark(name)` records a named checkpoint, and
 * `measure(name)` returns the delta in ms. Zero allocations inside
 * the hot path; the SSE loop can call `mark()` on every event frame
 * without bloating the flame graph.
 *
 * Usage:
 *
 *   import { startMark, mark, measure } from './clarityTimings';
 *   startMark();
 *   // user clicks Scan
 *   mark('first_visual');
 *   // first item arrives
 *   mark('first_item');
 *   console.log('TTFV', measure('first_visual'));  // 120ms
 *   console.log('TTFOD', measure('first_item'));   // 2340ms
 *
 * Integration with Clarity: report the deltas through
 * `clarityHelpers.trackFeature('scan', 'ttfv', { ms: String(…) })`
 * in the UploadScreen on event boundaries. Keeping this module
 * decoupled from Clarity lets us unit-test it without mocking the
 * whole analytics layer.
 */

/**
 * Map of named marks to their `performance.now()` timestamp
 * relative to the current session's `startMark()` anchor.
 *
 * Module-local — one measurement session active at a time. The scan
 * flow is single-user + single-image-at-a-time so this is fine; if
 * we ever run concurrent sessions we switch to an instance-per-
 * session `ClarityTimings` class.
 */
const marks = new Map<string, number>();
let sessionStart: number | null = null;

const now = (): number => {
  if (typeof performance !== 'undefined' && performance.now) {
    return performance.now();
  }
  // SSR / degraded runtime fallback.
  return Date.now();
};

/** Anchor a fresh measurement session. Must be called before any
 *  `mark()` / `measure()`. Safe to call repeatedly; each call
 *  resets every mark from the prior session. */
export function startMark(): void {
  sessionStart = now();
  marks.clear();
}

/** Record a named checkpoint at the current time. Idempotent —
 *  subsequent calls with the same name overwrite (use when an
 *  event fires more than once and only the latest is meaningful).
 *  Pass `onlyFirst: true` to make the first call win instead. */
export function mark(name: string, options?: { onlyFirst?: boolean }): number {
  if (sessionStart === null) startMark();
  if (options?.onlyFirst && marks.has(name)) return marks.get(name)!;
  const t = now() - (sessionStart ?? 0);
  marks.set(name, t);
  return t;
}

/** Return the elapsed ms between `startMark()` and the named
 *  checkpoint, or `null` if the mark was never recorded. */
export function measure(name: string): number | null {
  if (!marks.has(name)) return null;
  return Math.round(marks.get(name)!);
}

/** Return ms elapsed since `startMark()` (live, uses `now()`). */
export function elapsed(): number {
  if (sessionStart === null) return 0;
  return Math.round(now() - sessionStart);
}

/** Snapshot every named mark at once. Convenient for writing the
 *  scorecard state in the Done view. Omits marks that were never
 *  recorded (caller can `?? null` as needed). */
export function snapshot(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [name, t] of marks) out[name] = Math.round(t);
  return out;
}

/** Reset the session. Called implicitly by `startMark()` but
 *  exposed for test teardown. */
export function reset(): void {
  marks.clear();
  sessionStart = null;
}

/** Canonical mark names the scan flow uses. Importing this module
 *  constants-only avoids typos. */
export const TimingMark = {
  /** User clicked Scan / file picker resolved. */
  START: 'start',
  /** First DOM mutation after click — scanning hero visible. */
  FIRST_VISUAL: 'first_visual',
  /** First `item` SSE event parsed. */
  FIRST_ITEM: 'first_item',
  /** First `mask` SSE event parsed. */
  FIRST_MASK: 'first_mask',
  /** First mask actually rendered on the stage (rasterized). */
  FIRST_MASK_RENDERED: 'first_mask_rendered',
  /** Last `mask` SSE event parsed. */
  LAST_MASK: 'last_mask',
  /** `complete` SSE event parsed. */
  SCAN_COMPLETE: 'scan_complete',
  /** POST /inventory/confirm/batch resolved. */
  CONFIRM_DONE: 'confirm_done',
  /** First `primary_photo_ready` event parsed. */
  FIRST_PRIMARY: 'first_primary',
  /** Last `primary_photo_ready` event parsed. */
  LAST_PRIMARY: 'last_primary',
  /** Done view visible / rendered. */
  DONE: 'done',
} as const;

export type TimingMarkName = (typeof TimingMark)[keyof typeof TimingMark];
